import { TWeekDays } from "components/form/calendarField/helpers";

const WEEKDAY_TO_RRULE: Record<TWeekDays, string> = {
  Sunday: "SU",
  Monday: "MO",
  Tuesday: "TU",
  Wednesday: "WE",
  Thursday: "TH",
  Friday: "FR",
  Saturday: "SA",
};

const WEEKDAY_INDEX: Record<TWeekDays, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const EVENT_DURATION_HOURS = 4;

export interface ICalendarEventInput {
  uid: string;
  summary: string;
  location?: string;
  timeZone?: string;
  startDate: string;
  endDate?: string;
  startTime?: string;
  weekDay?: TWeekDays;
}

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function dateParts(date: string) {
  const [year, month, day] = date.split("-").map(Number);
  return { year, month, day };
}

function timeParts(time?: string) {
  const [hour = 0, minute = 0, second = 0] = (time || "00:00:00").split(":").map(Number);
  return { hour, minute, second };
}

function formatDate(year: number, month: number, day: number) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function formatTime(hour: number, minute: number, second: number) {
  return `${pad(hour)}:${pad(minute)}:${pad(second)}`;
}

function formatLocalStamp(date: string, time: string) {
  const { year, month, day } = dateParts(date);
  const { hour, minute, second } = timeParts(time);
  return `${year}${pad(month)}${pad(day)}T${pad(hour)}${pad(minute)}${pad(second)}`;
}

function addHours(date: string, time: string, hours: number) {
  const { year, month, day } = dateParts(date);
  const { hour, minute, second } = timeParts(time);
  const value = new Date(year, month - 1, day, hour, minute, second);
  value.setHours(value.getHours() + hours);
  return {
    date: formatDate(value.getFullYear(), value.getMonth() + 1, value.getDate()),
    time: formatTime(value.getHours(), value.getMinutes(), value.getSeconds()),
  };
}

function nextDay(date: string) {
  const { year, month, day } = dateParts(date);
  const value = new Date(year, month - 1, day);
  value.setDate(value.getDate() + 1);
  return formatDate(value.getFullYear(), value.getMonth() + 1, value.getDate());
}

function firstWeekdayOnOrAfter(startDate: string, weekDay: TWeekDays) {
  const { year, month, day } = dateParts(startDate);
  const value = new Date(year, month - 1, day);
  const delta = (WEEKDAY_INDEX[weekDay] - value.getDay() + 7) % 7;
  value.setDate(value.getDate() + delta);
  return formatDate(value.getFullYear(), value.getMonth() + 1, value.getDate());
}

function escapeIcsText(value: string) {
  return value.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
}

function wallTimeToUtcStamp(date: string, time: string, timeZone: string) {
  const { year, month, day } = dateParts(date);
  const { hour, minute, second } = timeParts(time);
  const utcMillis = Date.UTC(year, month - 1, day, hour, minute, second);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = formatter.formatToParts(new Date(utcMillis));
  const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value ?? "0");
  const zonedAsUtc = Date.UTC(read("year"), read("month") - 1, read("day"), read("hour"), read("minute"), read("second"));
  const instant = new Date(utcMillis - (zonedAsUtc - utcMillis));
  return (
    `${instant.getUTCFullYear()}${pad(instant.getUTCMonth() + 1)}${pad(instant.getUTCDate())}` +
    `T${pad(instant.getUTCHours())}${pad(instant.getUTCMinutes())}${pad(instant.getUTCSeconds())}Z`
  );
}

export function buildCalendarFile(event: ICalendarEventInput) {
  const timeZone = event.timeZone || "UTC";
  const startTime = event.startTime || "00:00:00";
  const endDate = event.endDate || event.startDate;
  const isRecurring = Boolean(event.weekDay);
  const startDate = isRecurring && event.weekDay ? firstWeekdayOnOrAfter(event.startDate, event.weekDay) : event.startDate;

  let endStampDate = startDate;
  let endStampTime = startTime;
  if (isRecurring) {
    const end = addHours(startDate, startTime, EVENT_DURATION_HOURS);
    endStampDate = end.date;
    endStampTime = end.time;
  } else if (endDate > event.startDate) {
    endStampDate = nextDay(endDate);
    endStampTime = "00:00:00";
  } else {
    const end = addHours(startDate, startTime, EVENT_DURATION_HOURS);
    endStampDate = end.date;
    endStampTime = end.time;
  }

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Tee-On//Event//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${escapeIcsText(event.uid)}`,
    `DTSTAMP:${wallTimeToUtcStamp(event.startDate, startTime, timeZone)}`,
    `SUMMARY:${escapeIcsText(event.summary)}`,
  ];

  if (event.location) {
    lines.push(`LOCATION:${escapeIcsText(event.location)}`);
  }

  if (timeZone === "UTC") {
    lines.push(`DTSTART:${wallTimeToUtcStamp(startDate, startTime, "UTC")}`);
    lines.push(`DTEND:${wallTimeToUtcStamp(endStampDate, endStampTime, "UTC")}`);
  } else {
    lines.push(`DTSTART;TZID=${timeZone}:${formatLocalStamp(startDate, startTime)}`);
    lines.push(`DTEND;TZID=${timeZone}:${formatLocalStamp(endStampDate, endStampTime)}`);
  }

  if (isRecurring && event.weekDay) {
    const until = wallTimeToUtcStamp(endDate, "23:59:59", timeZone);
    lines.push(`RRULE:FREQ=WEEKLY;BYDAY=${WEEKDAY_TO_RRULE[event.weekDay]};UNTIL=${until}`);
  }

  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n");
}

export function downloadCalendarFile(filename: string, contents: string) {
  const blob = new Blob([contents], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
